import React from "react";
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Icon,
  Table,
  Tbody,
  Tr,
  Td,
  useToast,
  Select,
  IconButton,
  Divider,
  Badge,
} from "@chakra-ui/react";
import {
  CheckIcon,
  CloseIcon,
  ArrowBackIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@chakra-ui/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchWithAuth } from "../utils/fetchWithAuth";
import UserMenu from "./UserMenu";

const CONTEXT_HEADLINES = {
  strategy: {
    title: "Your strategy needs room to grow",
    subtitle:
      "You've hit your strategy regeneration limit. Upgrade to let Pabs AI employees keep iterating, refining, and pushing your strategy forward..",
  },
  competitor: {
    title: "Your market picture is incomplete",
    subtitle:
      "You've hit your competitor analysis limit. Upgrade to keep your competitive intelligence sharp and your strategy grounded in reality.",
  },
  docs: {
    title: "The strategy is done. Your Pabs Ai employees can't write it up.",
    subtitle:
      "Your team has built the thinking, now they need capacity to turn it into living documents your whole company can act on.",
  },
  chat: {
    title:
      "Pabs AI strategy & Planning advisors are online. You can't reach them.",
    subtitle:
      "You've hit your daily conversation limit. Pro gives you unlimited access to your full team of AI design and strategy leads, whenever you need them.",
  },
  plans: {
    title: "You've outgrown your free plan",
    subtitle:
      "Each project gets its own dedicated AI team. Free gives you 3. Pro expands to 10 — with extra slots if you need more.",
  },
  default: {
    title: "Keep your strategy alive",
    subtitle:
      "Pabs AI employees can do more, if you let them have a full time job.\nUpgrade to Pro so we keep building, iterating, and keeping your strategy alive without interruption.",
  },
};


// Credit counts come from plan_configs (single source of truth, see
// utils/planTiers.js) via GET /api/subscription/pricing — the 30/300 here are
// only the fallback shown before that fetch resolves, or if it fails.
const buildAllFeatures = (freeCredits = 30, proCredits = 300) => [
  { label: "AI credits / month", free: `${freeCredits} credits`, pro: `${proCredits} credits` },
  { label: "Projects / Briefs", free: "3", pro: "10 + extra" },
  { label: "Strategy & Planning Chat", free: "25 / day", pro: "Unlimited" },
  { label: "Credit top-ups", free: true, pro: true },
  { label: "Connect to Vibe-Code apps", free: true, pro: true },
  { label: "Capture learnings automatically", free: true, pro: true },
  { label: "Schedule Strategy Pulses", free: true, pro: true },
  { label: "Customised brand shared views", free: false, pro: true },
  { label: "Enriched competitor data", free: false, pro: true },
  { label: "PM tool integrations", free: false, pro: true },
  { label: "Extra project slots (add-on)", free: false, pro: true },
  { label: "Full access to 4 AI Pabs employees", free: false, pro: true },
];

const VISIBLE_BY_DEFAULT = 7;

const FeatureValue = ({ value }) => {
  if (value === true)
    return <Icon as={CheckIcon} color="brand.700" boxSize={4} />;
  if (value === false)
    return <Icon as={CloseIcon} color="gray.300" boxSize={3} />;
  return (
    <Text fontSize="sm" color="gray.700" fontWeight="medium">
      {value}
    </Text>
  );
};

const UpgradePage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showAllFeatures, setShowAllFeatures] = React.useState(false);
  const [selectedPack, setSelectedPack] = React.useState(0);
  const [extraSlots, setExtraSlots] = React.useState(0);
  const [topupPacks, setTopupPacks] = React.useState([]);
  const [selectedTopupPack, setSelectedTopupPack] = React.useState(0);
  const [isTopupSubmitting, setIsTopupSubmitting] = React.useState(false);
  const [freeMonthlyCredits, setFreeMonthlyCredits] = React.useState(30);
  const [proMonthlyCredits, setProMonthlyCredits] = React.useState(300);

  React.useEffect(() => {
    fetchWithAuth("/api/subscription/topup-packs")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setTopupPacks(data);
          if (data.length > 0) setSelectedTopupPack(data[0].credits);
        }
      })
      .catch(() => {});
  }, []);

  React.useEffect(() => {
    fetch("/api/subscription/pricing")
      .then((r) => r.json())
      .then((data) => {
        if (Number.isFinite(data?.freeMonthlyCredits)) setFreeMonthlyCredits(data.freeMonthlyCredits);
        if (Number.isFinite(data?.proMonthlyCredits)) setProMonthlyCredits(data.proMonthlyCredits);
      })
      .catch(() => {});
  }, []);

  const context = searchParams.get("context") || "default";
  const { title, subtitle } =
    CONTEXT_HEADLINES[context] || CONTEXT_HEADLINES.default;

  const allFeatures = buildAllFeatures(freeMonthlyCredits, proMonthlyCredits);
  const visibleFeatures = showAllFeatures
    ? allFeatures
    : allFeatures.slice(0, VISIBLE_BY_DEFAULT);
  const hiddenCount = allFeatures.length - VISIBLE_BY_DEFAULT;

  const BASE_PRICE = 19.99;
  const EXTRA_SLOT_PRICE = 5;
  const totalMonthly = (BASE_PRICE + extraSlots * EXTRA_SLOT_PRICE).toFixed(2);

  const handleTopup = async () => {
    setIsTopupSubmitting(true);
    try {
      const response = await fetchWithAuth("/api/subscription/create-topup-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack: selectedTopupPack }),
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to start checkout");
      }
    } catch (error) {
      console.error("Top-up error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsTopupSubmitting(false);
    }
  };

  const handleUpgrade = async () => {
    setIsSubmitting(true);
    try {
      fetchWithAuth("/api/subscription/capture-upgrade-intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ triggerContext: context }),
      }).catch(() => {});
      const response = await fetchWithAuth(
        "/api/subscription/create-checkout-session",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ extraSlots, topupPack: selectedPack }),
        },
      );
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(data.error || "Failed to start checkout");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setIsSubmitting(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50">
      {/* Top bar */}
      <Box px={6} py={3}>
        <Flex align="center" justify="space-between">
          <HStack spacing={3}>
            <Button
              variant="ghost"
              leftIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              color="gray.500"
              fontWeight="normal"
              size="sm"
              _hover={{ color: "gray.800", bg: "gray.100" }}
            >
              Back
            </Button>
            <img
              src="/images/pablo_logo1.0.svg"
              alt="Pabs"
              style={{ height: "24px" }}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </HStack>
          <UserMenu />
        </Flex>
      </Box>

      <Box maxW="1100px" mx="auto" px={6} pt={4} pb={16}>
        {/* Hero */}
        <VStack spacing={2} mb={6} textAlign="center">
          <Text
            fontSize={{ base: "2xl", md: "3xl" }}
            fontWeight="700"
            color="gray.900"
            lineHeight="1.2"
          >
            {title}
          </Text>
          <Text fontSize="md" color="gray.500" maxW={{ base: "100%", md: "680px" }} whiteSpace="pre-line">
            {subtitle}
          </Text>
        </VStack>

        {/* ── Subscription pricing cards ─────────────────────────── */}
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={5}
          mb={8}
          align="stretch"
        >
          {/* Free card */}
          <Box
            flex={1}
            bg="white"
            borderRadius="3xl"
            border="1px"
            borderColor="gray.200"
            p={8}
            display="flex"
            flexDirection="column"
          >
            <HStack spacing={2} mb={4} align="center">
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Free
              </Text>
              <Badge
                fontSize="10px"
                fontWeight="700"
                px={2}
                py={0.5}
                borderRadius="md"
                textTransform="uppercase"
                bg="gray.100"
                color="gray.500"
                letterSpacing="wide"
              >
                Current plan
              </Badge>
            </HStack>
            <HStack align="baseline" mb={1}>
              <Text fontSize="4xl" fontWeight="800" color="gray.900">
                $0
              </Text>
              <Text fontSize="sm" color="gray.400">
                /month
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.400" mb={6}>
              Get started at no cost
            </Text>
            <VStack align="start" spacing={3} mb={6} flex={1}>
              <Text
                fontSize="xs"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Includes
              </Text>
              {[
                `${freeMonthlyCredits} AI credits / month`,
                "3 projects",
                "25 AI chats / day",
                "Credit top-ups available",
                "MCP integration",
                "Learning Engine",
                "Schedule Strategy Pulse",
              ].map((f, i) => (
                <HStack key={i} spacing={3}>
                  <Icon as={CheckIcon} color="gray.400" boxSize={3.5} />
                  <Text fontSize="sm" color="gray.600">
                    {f}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Freelance Jobs card — standalone top-up, no Pro required */}
          <Box
            flex={1}
            bg="white"
            borderRadius="3xl"
            border="1px"
            borderColor="gray.200"
            p={8}
            display="flex"
            flexDirection="column"
          >
            <HStack spacing={2} mb={4} align="center">
              <Text
                fontSize="sm"
                fontWeight="600"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Freelance Jobs
              </Text>
            </HStack>
            <HStack align="baseline" mb={1}>
              <Text fontSize="4xl" fontWeight="800" color="gray.900">
                Top-up
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.400" mb={6}>
              One-time. Never expire. Use them on any job.
            </Text>

            <Select
              size="sm"
              borderRadius="lg"
              bg="gray.50"
              border="1px"
              borderColor="gray.200"
              fontWeight="medium"
              value={selectedTopupPack}
              onChange={(e) => setSelectedTopupPack(Number(e.target.value))}
              mb={3}
              fontSize="sm"
            >
              {topupPacks.map((p) => (
                <option key={p.credits} value={p.credits}>
                  {p.credits.toLocaleString()} credits — NZ${p.priceNzd}
                  {p.bestValue ? " · best value" : ""}
                </option>
              ))}
              {topupPacks.length === 0 && (
                <option disabled>Loading packs...</option>
              )}
            </Select>

            <Button
              w="full"
              bg="gray.900"
              color="white"
              size="lg"
              borderRadius="xl"
              _hover={{ bg: "gray.700" }}
              fontWeight="700"
              onClick={handleTopup}
              isLoading={isTopupSubmitting}
              loadingText="Redirecting to checkout..."
              isDisabled={!selectedTopupPack}
              mb={6}
            >
              Buy Credits
            </Button>

            <VStack align="start" spacing={3} flex={1}>
              <Text
                fontSize="xs"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                How it works
              </Text>
              {[
                "Buy credits once, no subscription",
                "Credits never expire",
                "Spend on any job — scans, docs, pulses",
                "Light jobs cost less, heavy work costs more",
                "Same pool as your monthly credits",
              ].map((f, i) => (
                <HStack key={i} spacing={3}>
                  <Icon as={CheckIcon} color="gray.400" boxSize={3.5} />
                  <Text fontSize="sm" color="gray.600">{f}</Text>
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Pro card */}
          <Box
            flex={1}
            bg="white"
            borderRadius="3xl"
            border="2px"
            borderColor="brand.700"
            p={8}
            position="relative"
            boxShadow="0 4px 24px rgba(107, 70, 193, 0.12)"
          >
            <Text
              fontSize="sm"
              fontWeight="600"
              color="brand.700"
              textTransform="uppercase"
              letterSpacing="wide"
              mb={4}
            >
              Pro
            </Text>
            <HStack align="baseline" mb={1} spacing={2}>
              <Text fontSize="4xl" fontWeight="800" color="gray.900">
                NZ${totalMonthly}
              </Text>
              <Text fontSize="sm" color="gray.400">
                /month
              </Text>
            </HStack>
            <Text fontSize="sm" color="gray.500" mb={extraSlots > 0 ? 1 : 6}>
              Billed monthly · Cancel anytime
            </Text>
            {extraSlots > 0 && (
              <Text fontSize="xs" color="gray.400" mb={6}>
                NZ$19.99 base + {extraSlots} extra slot{extraSlots !== 1 ? 's' : ''} × NZ$5
              </Text>
            )}

            {/* Extra project slots row */}
            <Flex align="center" justify="space-between" mb={4}>
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  Need more briefs & projects?
                </Text>
                <Text fontSize="xs" color="gray.400">
                  +NZ$5/mo each
                </Text>
              </Box>
              <HStack spacing={0}>
                <IconButton
                  icon={<Text fontWeight="bold">−</Text>}
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  onClick={() => setExtraSlots((s) => Math.max(0, s - 1))}
                  isDisabled={extraSlots === 0}
                  aria-label="Decrease"
                  borderColor="gray.300"
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                  w="32px"
                  h="32px"
                  minW="32px"
                />
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color="gray.800"
                  w="32px"
                  textAlign="center"
                >
                  {extraSlots}
                </Text>
                <IconButton
                  icon={<Text fontWeight="bold">+</Text>}
                  size="sm"
                  variant="outline"
                  borderRadius="lg"
                  onClick={() => setExtraSlots((s) => Math.min(20, s + 1))}
                  aria-label="Increase"
                  borderColor="gray.300"
                  color="gray.600"
                  _hover={{ bg: "gray.100" }}
                  w="32px"
                  h="32px"
                  minW="32px"
                />
              </HStack>
            </Flex>

            {/* Top up credits row */}
            <Flex align="center" justify="space-between" mb={6}>
              <Box>
                <Text fontSize="sm" fontWeight="600" color="gray.800">
                  Add extra juice
                </Text>
                <Text fontSize="xs" color="gray.400">
                  One-time · never expire
                </Text>
              </Box>
              <Select
                size="sm"
                borderRadius="lg"
                bg="gray.50"
                border="1px"
                borderColor="gray.200"
                fontWeight="medium"
                value={selectedPack}
                onChange={(e) => setSelectedPack(Number(e.target.value))}
                _focus={{ borderColor: "brand.400" }}
                maxW="200px"
                fontSize="xs"
              >
                <option value={0}>{proMonthlyCredits} Credits (included)</option>
                {topupPacks.map((p) => (
                  <option key={p.credits} value={p.credits}>
                    +{p.credits.toLocaleString()} cr (+NZ${p.priceNzd})
                    {p.bestValue ? " · best value" : ""}
                  </option>
                ))}
              </Select>
            </Flex>

            {/* CTA */}
            <Button
              w="full"
              bg="gray.900"
              color="white"
              size="lg"
              borderRadius="xl"
              _hover={{ bg: "gray.700" }}
              fontWeight="700"
              onClick={handleUpgrade}
              isLoading={isSubmitting}
              loadingText="Redirecting to checkout..."
            >
              {extraSlots > 0
                ? `Upgrade to Pro · NZ$${totalMonthly}/mo →`
                : "Upgrade to Pro →"}
            </Button>
            {selectedPack > 0 && (
              <Text fontSize="xs" color="gray.600" textAlign="center" mt={2}>
                You'll start with{" "}
                <Text as="span" fontWeight="semibold">{proMonthlyCredits + selectedPack} credits</Text>
                {" "}({proMonthlyCredits} included + {selectedPack} top-up, one-time charge).
              </Text>
            )}

            {/* What's included list */}
            <VStack align="start" spacing={3} mt={6} pt={6} borderTop="1px" borderColor="gray.100">
              <Text
                fontSize="xs"
                fontWeight="600"
                color="gray.400"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                Everything in Free, plus
              </Text>
              {[
                `${proMonthlyCredits} AI credits / month`,
                "10 projects + extra slots add-on",
                "Priority AI processing",
                "Enriched competitor data",
                "Personalised branded public shares",
                "Full access to 4 AI Pabs employees",
                "PM tool integrations",
              ].map((f, i) => (
                <HStack key={i} spacing={3}>
                  <Icon as={CheckIcon} color="brand.700" boxSize={3.5} />
                  <Text fontSize="sm" color="gray.700" fontWeight="medium">
                    {f}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </Flex>

        {/* ── Feature comparison table ─────────────────────────────── */}
        <Box
          bg="white"
          borderRadius="2xl"
          border="1px"
          borderColor="gray.100"
          overflow="hidden"
        >
          <Box px={8} py={6} borderBottom="1px" borderColor="gray.100">
            <Text fontSize="md" fontWeight="700" color="gray.900">
              What your AI employees can do for you
            </Text>
            <Text fontSize="xs" color="gray.400" mt={1}>
              Credits are one shared pool — spend them on any job. Heavier AI work (like competitor research) draws more credits than lighter jobs (like a Pulse check).
            </Text>
          </Box>
          <Flex
            px={8}
            py={3}
            bg="gray.50"
            borderBottom="1px"
            borderColor="gray.100"
          >
            <Box flex={1} />
            <HStack flex={1} justify="space-around">
              <Text
                fontSize="xs"
                fontWeight="700"
                color="gray.500"
                textTransform="uppercase"
                letterSpacing="wide"
                w="80px"
                textAlign="right"
              >
                Free
              </Text>
              <Text
                fontSize="xs"
                fontWeight="700"
                color="brand.700"
                textTransform="uppercase"
                letterSpacing="wide"
                w="80px"
                textAlign="right"
              >
                Pro
              </Text>
            </HStack>
          </Flex>
          <Table variant="unstyled" size="sm">
            <Tbody>
              {visibleFeatures.map((feature, i) => (
                <Tr
                  key={i}
                  _even={{ bg: "gray.50" }}
                  borderBottom="1px"
                  borderColor="gray.100"
                >
                  <Td px={8} py={4} w="60%">
                    <Text fontSize="sm" color="gray.700">
                      {feature.label}
                    </Text>
                  </Td>
                  <Td textAlign="center" w="20%">
                    <Flex justify="center">
                      <FeatureValue value={feature.free} />
                    </Flex>
                  </Td>
                  <Td textAlign="center" w="20%">
                    <Flex justify="center">
                      <FeatureValue value={feature.pro} />
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          <Box
            px={8}
            py={4}
            borderTop="1px"
            borderColor="gray.100"
            textAlign="center"
          >
            <Button
              variant="ghost"
              size="sm"
              color="gray.500"
              fontWeight="500"
              rightIcon={
                showAllFeatures ? <ChevronUpIcon /> : <ChevronDownIcon />
              }
              onClick={() => setShowAllFeatures(!showAllFeatures)}
              _hover={{ color: "gray.700", bg: "gray.50" }}
            >
              {showAllFeatures
                ? "Show fewer features"
                : `Show all ${hiddenCount} more features`}
            </Button>
          </Box>
        </Box>

        {/* Footer */}
        <VStack spacing={1} mt={10} textAlign="center">
          <Text fontSize="xs" color="gray.400">
            Secure checkout via Stripe · Cancel anytime · No lock-in contracts
          </Text>
          <Text fontSize="xs" color="gray.400">
            Questions? Reach us at{" "}
            <Text as="span" color="gray.600">
              pablo@pabs.co.nz
            </Text>
          </Text>
        </VStack>
      </Box>
    </Box>
  );
};

export default UpgradePage;
